/**
 * StreamStatusPill — consistent "Live / Reconnecting / Stale — showing last known"
 * affordance for realtime surfaces backed by `useEventStream`.
 *
 * The pill NEVER lies. Stale is keyed to data-age, not just the socket: pass
 * `dataStale` from the hook and a healthy heartbeat over a frozen number renders
 * "Stale — showing last known", not "Live". The one rule:
 *
 *   Live         := connected AND NOT dataStale
 *   Reconnecting := (connecting OR reconnecting) AND NOT dataStale
 *   Stale        := error OR disconnected OR dataStale   (even on a live socket)
 *
 * Opaque tokens only (color-mix over surface vars, no rgba). Reduced-motion aware:
 * the reconnecting pulse is disabled under `prefers-reduced-motion: reduce`.
 */

import type { StreamConnectionState } from '../hooks/useEventStream';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

const React = (globalThis as any).React;

export type StreamVisualState = 'live' | 'reconnecting' | 'stale';

export interface StreamStatusPillProps {
  connectionState: StreamConnectionState;
  /** Data-age staleness from the hook. When true the pill reads Stale even if connected. */
  dataStale?: boolean;
  /** Optional retry handler; when provided and stale, the pill exposes a "Retry" action. */
  onRetry?: () => void;
  /** Override the default copy per state. */
  labels?: Partial<Record<StreamVisualState, string>>;
  className?: string;
}

const DEFAULT_LABELS: Record<StreamVisualState, string> = {
  live: 'Live',
  reconnecting: 'Reconnecting…',
  stale: 'Stale — showing last known',
};

const DOT_COLORS: Record<StreamVisualState, string> = {
  live: 'var(--color-success, #52c41a)',
  reconnecting: 'var(--color-warning, #faad14)',
  stale: 'var(--color-text-tertiary, #8c8c8c)',
};

const PULSE_KEYFRAMES_ID = 'esw-stream-pulse-keyframes';

export function deriveVisualState(
  connectionState: StreamConnectionState,
  dataStale?: boolean,
): StreamVisualState {
  if (dataStale) return 'stale';
  if (connectionState === 'connected') return 'live';
  if (connectionState === 'connecting' || connectionState === 'reconnecting') {
    return 'reconnecting';
  }
  // 'error' | 'disconnected'
  return 'stale';
}

function ensurePulseKeyframes() {
  try {
    const doc = (globalThis as any).document;
    if (!doc || doc.getElementById(PULSE_KEYFRAMES_ID)) return;
    const style = doc.createElement('style');
    style.id = PULSE_KEYFRAMES_ID;
    style.textContent =
      '@keyframes esw-stream-pulse{0%{opacity:1}50%{opacity:.35}100%{opacity:1}}';
    doc.head?.appendChild(style);
  } catch {
    /* SSR / no DOM — pulse simply won't animate */
  }
}

export function StreamStatusPill({
  connectionState,
  dataStale,
  onRetry,
  labels,
  className,
}: StreamStatusPillProps) {
  const reducedMotion = usePrefersReducedMotion();
  const visual = deriveVisualState(connectionState, dataStale);
  const label = (labels && labels[visual]) || DEFAULT_LABELS[visual];
  const dotColor = DOT_COLORS[visual];

  const animate = visual === 'reconnecting' && !reducedMotion;
  if (animate) ensurePulseKeyframes();

  const pillStyle: Record<string, string | number> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 10px',
    borderRadius: 999,
    // Opaque tint: mix the state color into an elevated surface (no rgba).
    backgroundColor: `color-mix(in srgb, ${dotColor} 12%, var(--color-bg-elevated, #ffffff))`,
    border: `1px solid color-mix(in srgb, ${dotColor} 28%, var(--color-bg-elevated, #ffffff))`,
    fontSize: 11,
    lineHeight: '16px',
    fontWeight: 600,
    color: 'var(--color-text-secondary, #595959)',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };

  const dotStyle: Record<string, string | number> = {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: dotColor,
    flexShrink: 0,
    animation: animate ? 'esw-stream-pulse 1.4s ease-in-out infinite' : 'none',
  };

  const children: any[] = [
    React.createElement('span', { key: 'dot', style: dotStyle, 'aria-hidden': true }),
    React.createElement('span', { key: 'label' }, label),
  ];

  if (onRetry && visual === 'stale') {
    children.push(
      React.createElement(
        'button',
        {
          key: 'retry',
          type: 'button',
          onClick: onRetry,
          style: {
            marginLeft: 4,
            padding: 0,
            border: 'none',
            background: 'none',
            color: 'var(--color-primary, #1677ff)',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'inherit',
            textDecoration: 'underline',
          },
          'aria-label': 'Retry stream connection',
        },
        'Retry',
      ),
    );
  }

  return React.createElement(
    'span',
    {
      className: className || undefined,
      style: pillStyle,
      role: 'status',
      'aria-live': 'polite',
      'data-testid': 'stream-status-pill',
      'data-state': visual,
      title: label,
    },
    ...children,
  );
}

export default StreamStatusPill;
